import { Icon } from "@inno-ui/icons";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import type { TimeSelectorProps } from "./time-slots.types";

import noTimeSlotImage from "../../assets/no-time-slots.png";
import { useDetectDevice } from "../../hooks/use-detect-device";
import { useAppointmentStore } from "../../store/appointment/appointment-store";
import { useGeneralStore } from "../../store/general/general-store";
import { Device } from "../../utils/enums";
import { formatCompleteTimezone, formatDate } from "../../utils/helpers/date";
import styles from "./time-slots.module.scss";

export function TimeSlots({ onSelectTime }: TimeSelectorProps) {
  const {
    setTimeRange,
    resetTimeRange,
    timeIntervals,
    appointmentData: { date, timeRange },
    isFetchingIntervals,
  } = useAppointmentStore();
  const { currentTimezone } = useGeneralStore();
  const device = useDetectDevice();
  const { t } = useTranslation();

  const timeRanges = timeIntervals[date]?.intervals?.map(interval => [interval.from, interval.to]);

  const handleOnClick = (range: Array<string>) => {
    const isSameRange = range[0] === timeRange[0] && range[1] === timeRange[1];
    if (isSameRange) {
      resetTimeRange();
    }
    else {
      onSelectTime?.();
      setTimeRange(range);
    }
  };

  return (
    <div className={styles.timeSlotsWrapper}>
      {isFetchingIntervals ? (
        <div className={styles.timeSlots}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
            {[...Array.from({ length: 5 })].map((_, index) => (
              <Skeleton key={index} height={45} width="100%" borderRadius={8} />
            ))}
          </div>
        </div>
      ) : (
        <>
          {!!date && <h3>{formatDate(date, currentTimezone)}</h3>}
          {/* formatted timezone in mobile only */}
          {device === Device.Mobile && (
            <div className={styles.timezone}>
              <Icon icon="GlobeFullIcon" color="var(--gray-400)" />
              {formatCompleteTimezone(currentTimezone)}
            </div>
          )}
          {timeRanges?.length
            ? (
                <div className={styles.timeSlots}>
                  {timeRanges?.map((range) => {
                    const rangeText = `${range[0]} - ${range[1]}${t("time-prefix")}`;
                    const isActive = range[0] === timeRange[0] && range[1] === timeRange[1];
                    return (
                      <button
                        type="button"
                        className={isActive ? styles.active : ""}
                        onClick={() => {
                          handleOnClick(range);
                        }}
                        key={rangeText}
                      >
                        {rangeText}
                      </button>
                    );
                  })}
                </div>
              )
            : (
                <div className={styles.noTimeSlots}>
                  <p>{t("no-time-slots-found")}</p>
                  <img src={noTimeSlotImage} />
                </div>
              )}
        </>
      )}
    </div>
  );
}
